// StoriesUploader — TikTok/Snap/FB-style story upload triggered from a profile avatar tap.
// Pops a sheet to record/pick a short photo or 60s video; on submit creates a regular
// `/posts/v2` entry tagged kind="story" so it appears at the top of the user's newsfeed.
import { useRef, useState } from "react";
import { Camera, X, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const readDataUrl = (f) => new Promise((res) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
});

// Probes a video file's real length client-side (no upload needed) so the story
// viewer can play it for its actual duration instead of a hardcoded guess.
const probeVideoDuration = (file) => new Promise((res) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(url); res(v.duration || null); };
  v.onerror = () => { URL.revokeObjectURL(url); res(null); };
  v.src = url;
});

export default function StoriesUploader({ open, onClose, onPosted }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Same double-submit race as the reel uploader had: disabled={submitting} only
  // takes effect once React re-renders, so a burst of rapid clicks/taps can all
  // fire submit() before that happens. A ref updates synchronously and catches it.
  const submittingRef = useRef(false);

  if (!open) return null;

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1_000_000_000) { toast.error("Story max 1000MB"); return; }
    const isVideo = f.type.startsWith("video/");
    const duration = isVideo ? await probeVideoDuration(f) : null;
    setPicked({ file: f, isVideo, preview: await readDataUrl(f), duration });
  };

  const submit = async () => {
    if (!user) return;
    if (!picked) { toast.error("Pick a photo or video first"); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setUploadPct(0);
    try {
      // Always upload to a real file, big or small — never inline base64 into
      // the post. media_url ends up in every feed/profile response with no
      // projection to exclude it, so a base64 blob there bloats every page
      // load for anyone who ever sees this story in their feed.
      const fd = new FormData(); fd.append("file", picked.file);
      const endpoint = picked.isVideo ? "/upload/video" : "/upload/image";
      // Progress feedback -- files up to 1000MB can take a while to send, and an
      // unlabeled spinner reads as "stuck" long before it actually is.
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const media_url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      await api.post("/posts/v2", {
        content: caption.trim() || "📍 Story",
        kind: "story",
        media_url,
        duration_seconds: picked.duration ? Math.min(120, Math.round(picked.duration)) : undefined,
      });
      toast.success("Story posted");
      setCaption(""); setPicked(null);
      onPosted?.(); onClose();
    } catch { toast.error("Could not post story"); }
    submittingRef.current = false;
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="stories-modal">
      <div className="bg-nexus-surface w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl border border-nexus-gold/30 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-nexus-gold">// new story · 24h</div>
            <h3 className="font-display font-black text-xl">Share a moment.</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!picked ? (
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="story-pick"
            className="w-full aspect-[9/16] rounded-2xl border-2 border-dashed border-white/15 hover:border-nexus-gold/60 transition-colors flex flex-col items-center justify-center gap-2 text-white/50 hover:text-white"
          >
            <Upload className="w-8 h-8" />
            <span className="text-sm font-display tracking-wider">Pick photo or 60s video</span>
            <span className="text-[10px] text-white/40">PNG / JPG / MP4 · up to 1000MB</span>
          </button>
        ) : (
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-[9/16] max-h-[60vh]">
            {picked.isVideo
              ? <video src={picked.preview} controls className="w-full h-full object-cover" />
              : <img src={picked.preview} alt="" className="w-full h-full object-cover" />}
            <button onClick={() => setPicked(null)} className="absolute top-2 right-2 bg-black/60 backdrop-blur p-1 rounded-full text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={pick} />
        <input
          value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)" maxLength={120}
          className="w-full bg-transparent border border-white/10 rounded-full px-4 py-2 text-sm"
          data-testid="story-caption"
        />
        <button onClick={submit} disabled={submitting || !picked} className="w-full rounded-full bg-gradient-to-r from-nexus-gold to-nexus-pink text-black font-display font-bold py-2.5 tracking-[0.3em] uppercase text-xs disabled:opacity-40" data-testid="story-submit">
          {submitting ? (uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Posting…") : "Post story"}
        </button>
      </div>
    </div>
  );
}
